import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { Form, Field, FormElement } from "@progress/kendo-react-form";
import { Label } from "@progress/kendo-react-labels";
import { Button } from "@progress/kendo-react-buttons";
import { TabStrip, TabStripTab } from "@progress/kendo-react-layout";
import { addNotificationWithTimeout } from '../../../app/addNotificationWithTimeout';
import { handleResponse } from '../../../app/handleResponse';
import { getCSRFToken } from '../../../app/getCSRFToken';
import { LoadingPanel } from "../../LoadingPanel";
import { AdministratorLoginShortingBlackoutDates } from "./LoginShortingBlackoutDates";
import { AdministratorLoginShortingDisabledPerformers } from "./LoginShortingDisabledPerformers";

import {
    FormInput,
    FormCheckbox,
    FormDropDownList,
    FormNumericTextBox
} from "../../forms/form-components";

import './Login.scss';

export const AdministratorLogin = (props) => {
    const dispatch = useDispatch();

    const [selectedTab, setSelectedTab] = useState(0);

    const handleSubmit = (dataItem) => {    
        let fetchMethod, successMessage;

        if (!dataItem.loginKey) {
            //Create new login
            fetchMethod = "POST";
            successMessage = "Created item successfully";
        }
        else {
            //Update existing login
            fetchMethod = "PUT";
            successMessage = "Updated item successfully";
        }

        dataItem.loginTypeId = dataItem.loginType.loginTypeId;
        dataItem.timeZoneId = dataItem.timeZone.timeZoneId;
        dataItem.twoFactorAuthenticationTypeId = dataItem.twoFactorAuthenticationType.twoFactorAuthenticationTypeId;

        fetch("/api/administrator/login",
            {
                method: fetchMethod,
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify(dataItem),
            })
            .then(handleResponse)
            .then(result => {
                addNotificationWithTimeout(dispatch, "success", successMessage);

                props.selectLogin(result.loginKey);
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);
            });
    }

    const deleteLogin = (loginKey) => {
        if (window.confirm("Are you sure you want to delete this item?") === false) {
            return;
        }

        const dataItem = {
            loginKey: loginKey
        }

        fetch("/api/administrator/login",
            {
                method: "DELETE",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify(dataItem),
            })
            .then(handleResponse)
            .then(result => {
                addNotificationWithTimeout(dispatch, "success", "Deleted item successfully");

                props.cancel();
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);
            });
    }

    const logOutAllDevices = (loginKey) => {
        if (window.confirm("Are you sure you log out all devices?") === false) {
            return;
        }

        const dataItem = {
            loginKey: loginKey,
        };

        fetch(`/api/administrator/logoutalldevices`,
            {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify(dataItem),
            })
            .then(handleResponse)
            .then(result => {
                addNotificationWithTimeout(dispatch, "success", "Logged out of all devices successfully");
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);
            });
    }

    const emailValidator = (value) => {
        const emailRegex = new RegExp(/\S+@\S+\.\S+/);

        if (emailRegex.test(value) === false) {
            return "A valid email address is required";
        }

        if (value.length > 100) {
            return "Maximum length is 100 characters";
        }

        return "";
    }

    const firstNameValidator = (value) => {
        if (!value) {
            return "This field is required";
        }

        if (value.length > 50) {
            return "Maximum length is 50 characters";
        }

        return "";
    }

    const lastNameValidator = (value) => {
        if (!value) {
            return "This field is required";
        }

        if (value.length > 50) {
            return "Maximum length is 50 characters";
        }

        return "";
    }

    const createPasswordValidator = (value) => {
        if (!value) {
            return "This field is required";
        }

        if (value.length < 8) {
            return "Minimum length is 8 characters";
        }

        if (value.length > 50) {
            return "Maximum length is 50 characters";
        }

        return "";
    }

    const updatePasswordValidator = (value) => {
        if (!value || value.length === 0) {
            return "";
        }

        if (value.length < 8) {
            return "Minimum length is 8 characters";
        }

        if (value.length > 50) {
            return "Maximum length is 50 characters";
        }

        return "";
    }

    const requiredValidator = (value) => {
        if (!value) {
            return "This field is required";
        }

        return "";
    }

    const tabStripSelectionChanged = (e) => {
        setSelectedTab(e.selected);
    };

    const getForm = (login) => {
        if (!login) {
            login = {
                isActive: true,
                isOnVacation: false,
                ignoreInventoryWhenShorting: false,
                loginType: props.loginTypes.find(element => element.loginTypeId === 1),
                timeZone: props.timeZones.find(element => element.timeZoneId === "UTC"),
                twoFactorAuthenticationType: props.twoFactorAuthenticationTypes.find(element => element.twoFactorAuthenticationTypeId === 1),
            }
        }
        else {
            login.loginType = props.loginTypes.find(element => element.loginTypeId === login.loginTypeId);
            login.timeZone = props.timeZones.find(element => element.timeZoneId === (login.timeZoneId ?? "UTC"));
            login.twoFactorAuthenticationType = props.twoFactorAuthenticationTypes.find(element => element.twoFactorAuthenticationTypeId === login.twoFactorAuthenticationTypeId);
        }

        if (!login?.loginKey && selectedTab > 0) {
            //Cannot have selected tab be a disabled one
            setSelectedTab(0);
        }
        
        return (
            <div className="administratorLogin">
                <TabStrip selected={selectedTab} onSelect={tabStripSelectionChanged}>
                    <TabStripTab title={"Login"}>
                        <Form
                            onSubmit={handleSubmit}
                            initialValues={login}
                            key={JSON.stringify(login)}
                            render={(formRenderProps) => (
                                <FormElement>
                                    <h2>
                                        {!login?.loginKey ? "New Login" : "Update Login"}
                                    </h2>
                                    <fieldset className={"k-form-fieldset"}>
                                        {!login?.loginKey ? "" :
                                            (<div className="mb-3 noFormField">
                                                <Label>Key:</Label> {login.loginKey}
                                            </div>)
                                        }

                                        {!login?.loginKey ? "" :
                                            (<div className="mb-3 noFormField">
                                                <Label>Created:</Label> {login.dateCreated}
                                            </div>)
                                        }

                                        <Field
                                            id={"isActive"}
                                            name={"isActive"}
                                            label={"Active"}
                                            component={FormCheckbox}
                                        />

                                        <Field
                                            id={"isOnVacation"}
                                            name={"isOnVacation"}
                                            label={"On vacation"}
                                            component={FormCheckbox}
                                        />

                                        <Field
                                            id={"ignoreInventoryWhenShorting"}
                                            name={"ignoreInventoryWhenShorting"}
                                            label={"Ignore current inventory when shorting"}
                                            component={FormCheckbox}
                                        />

                                        <Field
                                            id={"loginType"}
                                            name={"loginType"}
                                            label={"Login type"}
                                            component={FormDropDownList}
                                            data={props.loginTypes}
                                            textField="name"
                                            dataItemKey="loginTypeId"
                                            validator={requiredValidator}
                                        /> 

                                        <Field
                                            id={"twoFactorAuthenticationType"}
                                            name={"twoFactorAuthenticationType"}
                                            label={"Two factor authentication type"}
                                            component={FormDropDownList}
                                            data={props.twoFactorAuthenticationTypes}
                                            textField="name"
                                            dataItemKey="twoFactorAuthenticationTypeId"
                                            validator={requiredValidator}
                                        /> 
                            
                                        <Field
                                            id={"timeZone"}
                                            name={"timeZone"}
                                            label={"Time zone"}
                                            component={FormDropDownList}
                                            data={props.timeZones}
                                            textField="name"
                                            dataItemKey="timeZoneId"
                                            validator={requiredValidator}
                                        /> 

                                        <Field
                                            id={"firstName"}
                                            name={"firstName"}
                                            label={"First name"}
                                            component={FormInput}
                                            validator={firstNameValidator}
                                        />

                                        <Field
                                            id={"lastName"}
                                            name={"lastName"}
                                            label={"Last name"}
                                            component={FormInput}
                                            validator={lastNameValidator}
                                        />

                                        <Field
                                            id={"emailAddress"}
                                            name={"emailAddress"}
                                            label={"Email address"}
                                            type={"email"}
                                            component={FormInput}
                                            validator={emailValidator}
                                        />

                                        <Field
                                            id={"password"}
                                            name={"password"}
                                            label={"Password"}
                                            component={FormInput}
                                            validator={!login?.loginKey ? createPasswordValidator : updatePasswordValidator}
                                            type={"password"}
                                            hint={!login?.loginKey ? "Eight or more characters" : "Leave blank if not changing. Eight or more characters."}
                                            showPasswordToggle={true}
                                            autoComplete="new-password"
                                            aria-label={"Password"}                            
                                        />

                                        <Field
                                            id={"daysBeforeEventToDeleteShorts"}
                                            name={"daysBeforeEventToDeleteShorts"}
                                            label={"Days before event to delete shorts"}
                                            component={FormNumericTextBox}
                                            spinners={false}
                                            format="n0"
                                        />

                                        <Field
                                            id={"daysBeforeEventToCreateShorts"}
                                            name={"daysBeforeEventToCreateShorts"}
                                            label={"Days before event to create shorts"}
                                            component={FormNumericTextBox}
                                            spinners={false}
                                            format="n0"
                                        />

                                        <Field
                                            id={"shortingDefaultLowSeatNumber"}
                                            name={"shortingDefaultLowSeatNumber"}
                                            label={"Shorting default low seat number"}
                                            component={FormNumericTextBox}
                                            spinners={false}
                                            format="n0"
                                        />

                                        <Field
                                            id={"posBrokerId"}
                                            name={"posBrokerId"}
                                            label={"POS broker Id"}
                                            component={FormNumericTextBox}
                                            spinners={false}
                                            format="n0"
                                        />

                                        <Field
                                            id={"posAddressId"}
                                            name={"posAddressId"}
                                            label={"POS address Id"}
                                            component={FormNumericTextBox}
                                            spinners={false}
                                            format="n0"
                                        />

                                        <Field
                                            id={"posEmailId"}
                                            name={"posEmailId"}
                                            label={"POS email Id"}
                                            component={FormNumericTextBox}
                                            spinners={false}
                                            format="n0"
                                        />

                                        <Field
                                            id={"posPhoneId"}
                                            name={"posPhoneId"}
                                            label={"POS phone Id"}
                                            component={FormNumericTextBox}
                                            spinners={false}
                                            format="n0"
                                        />

                                        <Field
                                            id={"createEventsInPOS"}
                                            name={"createEventsInPOS"}
                                            label={"Create events in POS"}
                                            component={FormCheckbox}
                                        />

                                        <Field
                                            id={"removeEventsInPOS"}
                                            name={"removeEventsInPOS"}
                                            label={"Remove events in POS"}
                                            component={FormCheckbox}
                                        />
                                    </fieldset>
                                    <span className={"k-form-separator"} />
                                    <div className="k-form-buttons">
                                        <Button
                                            themeColor={"primary"}
                                            type={"submit"}
                                            disabled={!formRenderProps.allowSubmit}
                                            >
                                            Save
                                        </Button>

                                        {!login?.loginKey ? null : (
                                            <>
                                                <Button
                                                    type={"button"}
                                                    className={"deleteButton"}
                                                    onClick={() => deleteLogin(login.loginKey)}>
                                                    Delete
                                                </Button>

                                                <Button
                                                    type={"button"}
                                                    className={"deleteButton"}
                                                    onClick={() => logOutAllDevices(login.loginKey)}
                                                >
                                                    Log Out All Devices
                                                </Button>
                                            </>
                                        )}
                                    </div>
                                </FormElement>
                            )}
                        />
                    </TabStripTab>
                    <TabStripTab title={"Shorting Blackout Dates"} disabled={!login?.loginKey}>
                        <AdministratorLoginShortingBlackoutDates loginKey={login?.loginKey} />
                    </TabStripTab>
                    <TabStripTab title={"Shorting Disabled Performers"} disabled={!login?.loginKey}>
                        <AdministratorLoginShortingDisabledPerformers loginKey={login?.loginKey} />
                    </TabStripTab>
                </TabStrip>
                <div className="administratorLoginClose">
                    <Button
                        themeColor={"primary"}
                        type={"button"}
                        onClick={props.cancel}
                    >
                        Close
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <>
            { props.loading === true ? <LoadingPanel /> : (props.createNewLogin === true ? getForm() : (props.login ? getForm(props.login) : <div></div>))}
        </>
    );
}